import { useState, useMemo } from "react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserProfile } from "./useUserProfile";
import type { ConstanteData } from "../types/preEmployment.types";
import {
  STATUS_OPTIONS,
  CLINIC_INFO,
  generateCertificateNumber,
  extractDateOfBirth,
  extractBirthPlace,
} from "../utils/preEmploymentUtils";
import { usePdfRenderer } from "./usePdfRenderer";
import { toast } from "react-hot-toast";
import CertificatePDF from "../pdf/CertificatePDF";

export const usePreEmployment = () => {
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [showCertificate, setShowCertificate] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { userData, loading: userLoading } = useUserProfile();

  // Récupérer les données depuis la navigation
  const { consultationData } = location.state || {};

  // Construire les données du certificat (memoized)
  const constanteData: ConstanteData = useMemo(
    () => ({
      patient: {
        firstName:
          consultationData?.patientInfo?.prenom ||
          consultationData?.prenom ||
          consultationData?.firstName ||
          consultationData?.patientInfo?.firstName ||
          "Prénom non défini",
        lastName:
          consultationData?.patientInfo?.nom ||
          consultationData?.nom ||
          consultationData?.lastName ||
          consultationData?.patientInfo?.lastName ||
          "Nom non défini",
        dateOfBirth: extractDateOfBirth(consultationData),
        birthPlace: extractBirthPlace(consultationData),
      },
      doctor: {
        firstName: userData?.firstName || "Dr",
        lastName: userData?.lastName || "Docteur",
        title: userData?.title || "Dr",
      },
      examinationDate: new Date().toLocaleDateString("fr-FR", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
      certificateNumber: generateCertificateNumber(),
      clinic: CLINIC_INFO,
    }),
    [consultationData, userData]
  );

  // Gestionnaires d'événements
  const handleStatusSelect = (statusId: string) => {
    setSelectedStatus(statusId);
  };

  const { download } = usePdfRenderer();

  const handleGenerateCertificate = async () => {
    if (selectedStatus) {
      setShowCertificate(true);

      // Attempt to auto-download the certificate PDF, then redirect to dashboard
      try {
        const fileName = `Certificat_Medical_${constanteData.patient.lastName.replace(
          /[^a-zA-Z0-9]/g,
          "_"
        )}_${Date.now()}.pdf`;
        await download(
          React.createElement(CertificatePDF, {
            constanteData,
            selectedStatus,
          }),
          fileName
        );

        // Show success toast, wait a short moment to allow browser to start saving,
        // then redirect to dashboard.
        toast.success("Certificat téléchargé avec succès");
        await new Promise((res) => setTimeout(res, 700)); // 700ms
        navigate("/doctor/dashboard/constante");
      } catch (error) {
        console.error("Auto-download failed:", error);
        toast.error(
          "Échec du téléchargement du certificat. Veuillez réessayer."
        );
        // Do not redirect on failure; allow the user to retry or return manually
      }
    }
  };

  const handleBack = () => {
    navigate(-1); // Retour à la page précédente dans l'historique de navigation
  };

  const handleBackFromCertificate = () => {
    setShowCertificate(false);
  };

  const handleReturnToDashboard = () => {
    navigate("/doctor/dashboard/constante");
  };

  return {
    selectedStatus,
    showCertificate,
    userLoading,
    constanteData,
    statusOptions: STATUS_OPTIONS,

    handleStatusSelect,
    handleGenerateCertificate,
    handleBack,
    handleBackFromCertificate,
    handleReturnToDashboard,
  };
};
